import { useEffect, useState } from "react";
import type { LowBmiAssessment } from "@care-by-ella/contracts";
import { fetchLowBmiAssessment } from "@/lib/low-bmi-api";
import {
  conditionNoteLines,
  lowBmiLines,
  whtrHeadline,
} from "@/lib/low-bmi-lines";

// TES-1116 (TES-1112 point 8) — the doctor's block for a NEW_TREATMENT
// patient with BMI 25,0–26,9: WHtR with the measures, date and source behind
// it, Kirill's possible indicators, and her other answers from F-02's
// low-BMI section. The same pattern as ComorbidityAssessmentPanel: one
// component, on the patient page and in the meeting view.
//
// Renders nothing for a patient whose latest F-02 has no answer from the
// section — every other patient — so neither page grows an empty card.
// Indicators only: A-02 "Möjliga indikatorer markeras som underlag som
// kräver läkarbedömning, inte som uppfylld indikation eller automatiskt
// godkännande."

export function LowBmiAssessmentPanel({
  patientId,
  compact = false,
}: {
  patientId: string;
  /** Meeting view: no outer card chrome. */
  compact?: boolean;
}) {
  const [data, setData] = useState<LowBmiAssessment | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchLowBmiAssessment(patientId)
      .then((d) => alive && setData(d))
      .catch(
        (e: unknown) =>
          alive &&
          setError(e instanceof Error ? e.message : "Kunde inte hämta."),
      );
    return () => {
      alive = false;
    };
  }, [patientId]);

  // A failed read is shown; a patient without the section is not.
  if (error) {
    return <p className="text-sm text-danger">BMI 25,0–26,9: {error}</p>;
  }
  if (!data?.section) return null;
  const { measures, indicators, conditionNotes } = data.section;
  const noteLines = conditionNoteLines(conditionNotes);

  const body = (
    <div className="flex flex-col gap-sm">
      <p className="font-display text-[20px] leading-7">
        {whtrHeadline(measures)}
      </p>
      <div>
        <h3 className="mb-xs text-xs font-semibold uppercase tracking-[0.08em] text-textSecondary">
          Möjliga indikatorer – kräver läkarbedömning
        </h3>
        {indicators.length === 0 ? (
          <p className="text-sm text-textSecondary">
            Inga av Kirills indikatorer framgår av svaren.
          </p>
        ) : (
          <ul className="flex flex-col gap-xs">
            {indicators.map((i) => (
              <li
                key={i.id}
                className="rounded-md bg-warning/10 px-sm py-xs text-sm"
              >
                <span className="font-semibold">{i.label}</span>
                <span className="text-textSecondary"> · {i.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* Ö-346c — earlier or uncertain PMOS/hypertension/fatty liver: shown
          beside the indicators, never as one. */}
      {noteLines.length > 0 ? (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-md gap-y-xs rounded-md bg-surfaceMuted/40 px-sm py-xs text-sm">
          {noteLines.map((l) => (
            <div key={l.label} className="contents">
              <dt className="text-textSecondary">{l.label}</dt>
              <dd>{l.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      <dl className="grid grid-cols-[max-content_1fr] gap-x-md gap-y-xs text-sm">
        {lowBmiLines(measures, data.intake).map((l) => (
          <div key={l.label} className="contents">
            <dt className="text-textSecondary">{l.label}</dt>
            <dd>{l.value}</dd>
          </div>
        ))}
      </dl>
      {data.intake ? (
        <p className="text-xs text-textSecondary">
          Källa: F-02 v{data.intake.templateVersion}, inskickad{" "}
          {new Date(data.intake.submittedAt).toLocaleDateString("sv-SE")}.
          Systemet godkänner aldrig behandling automatiskt.
        </p>
      ) : null}
    </div>
  );

  if (compact) return body;
  return (
    <section className="rounded-md border border-surfaceMuted bg-surface p-md">
      <h2 className="mb-xs font-display text-lg">
        BMI 25,0–26,9: midja/längd-kvot och möjliga indikatorer
      </h2>
      {body}
    </section>
  );
}
